<template>
  <t-dropdown variant="open-below-right-fit"
              :show.sync="showDropdown"
              v-on-clickaway="close"
  >
    <div slot="trigger" slot-scope="{ mousedownHandler, blurHandler, isShown }">
      <base-button variant="primary"
                   class="test-export-statistics"
                   :class="isShown ? 'opacity-90' : ''"
                   @mousedown="mousedownHandler"
                   @blur="blurHandler"
      >
        Export statistics
      </base-button>
    </div>

    <div class="export-statistics-panel flex flex-col bg-white rounded shadow-xl p-5">
      <span class="text-center font-semibold text-gray-dark mb-4">Export statistics</span>

      <div class="flex gap-6">
        <div class="flex flex-col flex-1">
          <div class="flex items-center justify-center text-sm text-gray-dark mb-1">
            <date-from-icon class="mr-2" />
            <span>Date from:</span>
          </div>
          <t-datepicker :value="localDateFrom"
                        :clearable="false"
                        placeholder="Select date"
                        @input="localDateFrom = $event"
          />
        </div>
        <div class="flex flex-col flex-1">
          <div class="flex items-center justify-center text-sm text-gray-dark mb-1">
            <date-to-icon class="mr-2" />
            <span>Date to:</span>
          </div>
          <t-datepicker :value="localDateTo"
                        :clearable="false"
                        placeholder="Select date"
                        @input="localDateTo = $event"
          />
        </div>
      </div>

      <div class="mt-5">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center text-sm text-gray-dark">
            <bot-icon class="mr-2" />
            <span>Bots:</span>
          </div>
          <span class="text-xs text-gray-dark">{{ selectedBotIds.length }} of {{ bots.length }} selected</span>
        </div>

        <search-input v-if="bots.length > 5"
                      :value="search"
                      class="mb-2"
                      @input="search = $event"
        />

        <div v-if="showQuickSelect" class="flex gap-2 mb-2">
          <base-button variant="transparent-small" @click="toggleAll">{{ allButtonLabel }}</base-button>
          <template v-if="hasMixedTypes">
            <base-button variant="transparent-small" @click="toggleChat">{{ chatButtonLabel }}</base-button>
            <base-button variant="transparent-small" @click="toggleEmail">{{ emailButtonLabel }}</base-button>
          </template>
        </div>

        <div class="border border-solid border-gray-a10 rounded max-h-84 overflow-auto">
          <checkbox-dropdown v-if="checkboxOptions.length"
                             :options="checkboxOptions"
                             :checked-values="selectedBotIds"
                             :hide-footer="true"
                             @change="onSelectionChange"
          />
          <p v-else class="text-sm text-gray-dark text-center py-6 m-0">No bots match your search.</p>
        </div>
      </div>

      <div class="flex gap-3 mt-5">
        <base-button variant="bordered-icon-gray" class="flex-1 justify-center" @click="close">
          Cancel
        </base-button>
        <busy-button class="flex-1 justify-center"
                     :id="BUSY_BUTTONS.STATS_EXPORT"
                     :is-loading="isExporting"
                     :is-disabled="isExportDisabled"
                     variant="primary"
                     @click="exportCsv"
        >
          Export
        </busy-button>
      </div>
    </div>
  </t-dropdown>
</template>

<script>
import { directive as onClickaway } from 'vue-clickaway'
import moment from 'moment'
import BaseButton from '@/components/base-components/BaseButton'
import SearchInput from '@/components/base-components/SearchInput'
import CheckboxDropdown from '@/components/common/CheckboxDropdown'
import BusyButton from '@/components/common/BusyButton'
import DateFromIcon from '@/assets/images/icons/DateFromIcon'
import DateToIcon from '@/assets/images/icons/DateToIcon'
import BotIcon from '@/assets/images/icons/BotIcon'
import statsExportService from '@/services/amio/stats-export.service'
import csvDownloadUtil from '@/utils/csv-download.util'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import toaster from '@/utils/toaster'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import { BOT_TYPES } from '@/utils/bot.constants'

// normalize a (possibly ISO) date string to the 'YYYY-MM-DD' the date picker expects
function toDay(value) {
  return value ? moment(value).format('YYYY-MM-DD') : null
}

export default {
  name: 'ExportStatisticsMenu',

  components: {
    BaseButton,
    SearchInput,
    CheckboxDropdown,
    BusyButton,
    DateFromIcon,
    DateToIcon,
    BotIcon
  },

  directives: {
    onClickaway
  },

  props: {
    bots: {
      type: Array,
      default: () => []
    },
    multiBots: {
      type: Array,
      default: () => []
    },
    dateFrom: {
      type: String,
      default: ''
    },
    dateTo: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      BUSY_BUTTONS,
      showDropdown: false,
      selectedBotIds: this.bots.map(bot => bot.id),
      localDateFrom: toDay(this.dateFrom),
      localDateTo: toDay(this.dateTo),
      search: ''
    }
  },

  computed: {
    showQuickSelect() {
      return this.bots.length > 5
    },

    hasMixedTypes() {
      const types = new Set(this.bots.map(bot => bot.type))
      return types.size > 1
    },

    chatBotIds() {
      return this.bots
        .filter(bot => bot.type === BOT_TYPES.CHAT)
        .map(bot => bot.id)
    },

    emailBotIds() {
      return this.bots
        .filter(bot => bot.type === BOT_TYPES.EMAIL)
        .map(bot => bot.id)
    },

    areAllBotsSelected() {
      return this.bots.length > 0 && this.selectedBotIds.length === this.bots.length
    },

    areAllChatSelected() {
      return this.chatBotIds.length > 0 && this.chatBotIds.every(id => this.selectedBotIds.includes(id))
    },

    areAllEmailSelected() {
      return this.emailBotIds.length > 0 && this.emailBotIds.every(id => this.selectedBotIds.includes(id))
    },

    allButtonLabel() {
      return this.areAllBotsSelected ? 'Deselect all' : 'Select all'
    },

    chatButtonLabel() {
      return this.areAllChatSelected ? 'Deselect all chat' : 'Select all chat'
    },

    emailButtonLabel() {
      return this.areAllEmailSelected ? 'Deselect all email' : 'Select all email'
    },

    // multiBot keyed by the market/variant bot it owns (same idea as SettingsBots multiBotsByBotId)
    multiBotsByPrimaryBotId() {
      return this.multiBots.reduce((acc, multiBot) => {
        const primaryBotId = multiBot.primaryBotId || multiBot.primary_bot_id
        if (primaryBotId) acc[primaryBotId] = multiBot
        return acc
      }, {})
    },

    botsById() {
      return this.bots.reduce((acc, bot) => {
        acc[bot.id] = bot
        return acc
      }, {})
    },

    checkboxOptions() {
      const term = this.search.trim().toLowerCase()
      const options = this.bots
        .map(bot => ({
          id: bot.id,
          name: this.getBotDisplayName(bot)
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
      if (!term) return options
      return options.filter(option => option.name.toLowerCase().includes(term))
    },

    isInvalidDateRange() {
      return !!this.localDateFrom && !!this.localDateTo && this.localDateFrom > this.localDateTo
    },

    isExportDisabled() {
      return this.selectedBotIds.length === 0 ||
        !this.localDateFrom ||
        !this.localDateTo ||
        this.isInvalidDateRange
    },

    isExporting() {
      return busyButtonsModule.isButtonBusy(BUSY_BUTTONS.STATS_EXPORT)
    }
  },

  watch: {
    showDropdown(open) {
      if (open) this.resetState()
    },

    bots() {
      if (this.showDropdown) this.resetState()
    }
  },

  methods: {
    // Multi-bot markets: "{primaryBot.name} - {multiBot.name}". Standalone bots keep bot.name.
    getBotDisplayName(bot) {
      const multiBot = this.multiBotsByPrimaryBotId[bot.id]
      if (!bot.multi_bot_id || !multiBot) return bot.name || ''

      const primaryBot = this.botsById[bot.primary_bot_id] ||
        this.bots.find(candidate => candidate.multi_bot_id === bot.multi_bot_id && candidate.is_primary_variant) ||
        bot
      return `${primaryBot.name} - ${multiBot.name}`
    },

    resetState() {
      this.selectedBotIds = this.bots.map(bot => bot.id)
      this.localDateFrom = toDay(this.dateFrom)
      this.localDateTo = toDay(this.dateTo)
      this.search = ''
    },

    close() {
      this.showDropdown = false
    },

    onSelectionChange(ids) {
      this.selectedBotIds = ids
    },

    toggleAll() {
      this.selectedBotIds = this.areAllBotsSelected ? [] : this.bots.map(bot => bot.id)
    },

    toggleScope(botIds) {
      const allScopeSelected = botIds.every(id => this.selectedBotIds.includes(id))
      if (allScopeSelected) {
        this.selectedBotIds = this.selectedBotIds.filter(id => !botIds.includes(id))
        return
      }

      this.selectedBotIds = [...new Set([...this.selectedBotIds, ...botIds])]
    },

    toggleChat() {
      this.toggleScope(this.chatBotIds)
    },

    toggleEmail() {
      this.toggleScope(this.emailBotIds)
    },

    buildFilename() {
      const fmt = (date, fallback) => (date ? moment(date) : fallback).format('YYYYMMDD')
      const from = fmt(this.localDateFrom, moment.utc(0))
      const to = fmt(this.localDateTo, moment())
      return `bot-statistics-${from}-${to}.csv`
    },

    async exportCsv() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.STATS_EXPORT, true)
      toaster.info('Generating your CSV — this may take a couple of seconds.')
      try {
        const blob = await statsExportService.exportBotStatsCsv({
          botIds: this.selectedBotIds,
          dateFrom: this.localDateFrom,
          dateTo: this.localDateTo
        })
        csvDownloadUtil.downloadBlob(blob, this.buildFilename())
        this.close()
      } catch (err) {
        toaster.error(err)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.STATS_EXPORT, false)
      }
    }
  }
}
</script>

<style scoped>
.export-statistics-panel {
  width: 640px;
}
</style>
